import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useWebSocket } from '../hooks/useWebSocket';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import './Chat.css';

function Chat() {
  const [messages, setMessages] = useState([]);
  const [sessionID, setSessionID] = useState(null);
  const [isAuthed, setIsAuthed] = useState(false);
  const [hasMoreHistory, setHasMoreHistory] = useState(false);
  const [historyCursor, setHistoryCursor] = useState(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [hasLoadedInitialHistory, setHasLoadedInitialHistory] = useState(false);
  const [userName, setUserName] = useState(localStorage.getItem('name') || null);

  // Background prefetch UX (server warms caches after auth)
  const [prefetchState, setPrefetchState] = useState({ status: 'idle', steps: {} });

  // Track which WS history requests are currently in flight
  const historyInFlightRef = useRef(false);
  const pendingHistoryRequestIdRef = useRef(null);
  const connectedSessionIdRef = useRef(null);
  const authedRef = useRef(false);

  // Voice mode state
  const [voiceEnabled, setVoiceEnabled] = useState(() => {
    try { return JSON.parse(localStorage.getItem('voice_enabled') || 'false'); } catch { return false; }
  });
  const [listenMode, setListenMode] = useState(() => localStorage.getItem('voice_listen_mode') || 'ptt'); // 'ptt' | 'auto'
  const [speakReplies, setSpeakReplies] = useState(() => {
    try { return JSON.parse(localStorage.getItem('voice_speak_replies') || 'true'); } catch { return true; }
  });
  const [inputValue, setInputValue] = useState('');
  const [isRecognizing, setIsRecognizing] = useState(false);
  const [interimText, setInterimText] = useState('');

  const recognitionRef = useRef(null);
  const finalBufferRef = useRef('');
  const restartAfterTTSRef = useRef(false);

  // Get patient ID from URL params or localStorage (in production, use proper auth)
  const params = new URLSearchParams(window.location.search);
  const rawPatientID =
    params.get('patient_id') ||
    params.get('rcp_core_uid') ||
    localStorage.getItem('patient_id') ||
    localStorage.getItem('rcp_core_uid') ||
    '';

  // Local dev convenience: if no patient_id is provided, use a stable demo id so
  // the WebSocket can still connect.
  const patientID = rawPatientID || ((window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') ? 'demo' : '');

  // NOTE: We still maintain a session_id for the server to tag new chat messages,
  // but history is loaded patient-wide by default.
  const sessionStorageKey = patientID ? `hcbot_session_id:${patientID}` : null;
  const persistedSessionID = sessionStorageKey ? localStorage.getItem(sessionStorageKey) : null;

  const { socket, sendMessage, connectionStatus } = useWebSocket(patientID, persistedSessionID);
  const isConnected = connectionStatus === 'connected';

  // Persist the currently selected patient ID so reloads use the latest
  useEffect(() => {
    if (patientID) {
      localStorage.setItem('patient_id', patientID);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientID]);

  // When switching to a different patient, reset UI and history state
  useEffect(() => {
    setMessages([]);
    setSessionID(null);
    setIsAuthed(false);
    authedRef.current = false;
    setHistoryCursor(null);
    setHasLoadedInitialHistory(false);

    // Clear previous session binding when switching patient.
    if (sessionStorageKey) {
      try { localStorage.removeItem(sessionStorageKey); } catch { /* ignore */ }
    }
  }, [patientID, sessionStorageKey]);

  // When the socket disconnects/reconnects, reset auth/history request bookkeeping.
  useEffect(() => {
    if (connectionStatus !== 'connected') {
      setIsAuthed(false);
      authedRef.current = false;
      historyInFlightRef.current = false;
      pendingHistoryRequestIdRef.current = null;
      setIsLoadingHistory(false);
    }
  }, [connectionStatus]);

  useEffect(() => {
    console.log('[Chat] connection status:', connectionStatus);
  }, [connectionStatus]);

  useEffect(() => {
    const n = localStorage.getItem('name');
    if (n && n !== userName) setUserName(n);
  }, [connectionStatus, userName]);

  const requestHistoryPage = useCallback((opts = {}) => {
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      console.warn('[Chat] cannot request history: websocket not open');
      return;
    }
    if (!authedRef.current) {
      console.warn('[Chat] cannot request history: websocket not authenticated yet');
      return;
    }
    if (historyInFlightRef.current) return;
    historyInFlightRef.current = true;
    setIsLoadingHistory(true);

    const requestId = `hist-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    pendingHistoryRequestIdRef.current = requestId;

    const payload = {
      type: 'history_request',
      request_id: requestId,
    };

    //if (opts.session_id) payload.session_id = opts.session_id;
    if (opts.before_id) payload.before_id = opts.before_id;
    if (opts.limit) payload.limit = opts.limit;

    sendMessage(payload);
  }, [socket, sendMessage]);

  const loadInitialHistory = useCallback((sessionIdFromServer) => {
    if (hasLoadedInitialHistory) return;
    // For initial load, default server behavior uses WebInitialHistoryCount.
    requestHistoryPage({ session_id: sessionIdFromServer || sessionID || undefined });
  }, [hasLoadedInitialHistory, requestHistoryPage, sessionID]);

  // Speech synthesis (TTS)
  const ttsSupported = typeof window !== 'undefined' && typeof window.speechSynthesis !== 'undefined';
  const speakText = useCallback((text) => {
    if (!ttsSupported || !text) return;
    try {
      // Pause STT while speaking to prevent echo in auto mode
      if (isRecognizing) {
        restartAfterTTSRef.current = (voiceEnabled && listenMode === 'auto');
        stopRecognition();
      }
      window.speechSynthesis.cancel();
      const utter = new SpeechSynthesisUtterance(text);
      utter.lang = 'en-US';
      utter.rate = 1.0; // could be configurable
      utter.pitch = 1.0;
      utter.onend = () => {
        if (restartAfterTTSRef.current) {
          restartAfterTTSRef.current = false;
          if (voiceEnabled && listenMode === 'auto') {
            startRecognition();
          }
        }
      };
      window.speechSynthesis.speak(utter);
    } catch (e) {
      console.warn('TTS error', e);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ttsSupported, isRecognizing, voiceEnabled, listenMode]);

  const handleWebSocketMessage = useCallback((data) => {
    switch (data.type) {
      case 'connected':
        setSessionID(data.session_id);
        connectedSessionIdRef.current = data.session_id;

        // Persist session id so a refresh resumes the same session/thread.
        if (sessionStorageKey && data.session_id) {
          try { localStorage.setItem(sessionStorageKey, data.session_id); } catch { /* ignore */ }
        }

        // Add a system message indicating connection
        setMessages(prev => [...prev, {
          id: `system-${Date.now()}`,
          type: 'system',
          text: data.message,
          timestamp: new Date().toISOString(),
          channel: 'web',
        }]);
        break;

      case 'auth_ok':
        authedRef.current = true;
        setIsAuthed(true);
        if (data.name) {
          try {
            localStorage.setItem('name', data.name);
          } catch { /* ignore */ }
        }
        // After auth is confirmed, bootstrap the UI with history.
        if (!hasLoadedInitialHistory) {
          // Load patient-wide history (no session_id filter). The backend still uses the
          // WS connection's patient_id and cursor for pagination.
          loadInitialHistory(undefined);
        }
        break;

      case 'prefetch_started':
        setPrefetchState({ status: 'running', steps: {} });
        break;

      case 'prefetch_progress':
        setPrefetchState(prev => ({
          status: 'running',
          steps: {
            ...(prev?.steps || {}),
            [data.step || `step-${Date.now()}`]: {
              ok: Boolean(data.ok),
              duration_ms: data.duration_ms,
              timestamp: data.timestamp,
            }
          }
        }));
        break;

      case 'prefetch_done':
        setPrefetchState(prev => ({
          ...(prev || {}),
          status: (data.ok ? 'done' : 'error'),
          finishedAt: data.timestamp,
          duration_ms: data.duration_ms,
          errors: data.errors || [],
        }));
        break;

      case 'history_response': {
        // If request_id is present, only process the most recent in-flight request.
        if (data.request_id && pendingHistoryRequestIdRef.current && data.request_id !== pendingHistoryRequestIdRef.current) {
          break;
        }

        historyInFlightRef.current = false;
        pendingHistoryRequestIdRef.current = null;
        setIsLoadingHistory(false);

        if (data.error) {
          console.error('[Chat] history_response error:', data.error);
          historyInFlightRef.current = false;
          pendingHistoryRequestIdRef.current = null;
          break;
        }

        const historyMessages = (data.messages || []).map(m => ({
          id: m.id,
          type: m.type,
          text: m.text,
          disclaimer: m.disclaimer,
          flags: m.flags,
          timestamp: m.timestamp,
          channel: m.channel,
        }));

        // History comes newest->oldest; reverse to show oldest at top
        historyMessages.reverse();

        // Server returns newest->oldest; after reversing, we prepend older messages.
        setMessages(prev => [...historyMessages, ...prev]);

        setHasMoreHistory(Boolean(data.has_more));
        setHistoryCursor(data.next_cursor || null);
        setHasLoadedInitialHistory(true);
        break;
      }

      case 'chat_response':
        setMessages(prev => [...prev, {
          id: data.id || `bot-${Date.now()}`,
          type: 'bot',
          text: data.answer,
          disclaimer: data.disclaimer,
          flags: data.flags,
          timestamp: data.timestamp,
          channel: 'web',
        }]);
        if (voiceEnabled && speakReplies) {
          speakText(data.answer);
        }
        break;

      case 'typing':
        // Show typing indicator
        break;

      case 'error':
        // If we attempted a history request too early (shouldn't happen if isAuthed gates),
        // clear the in-flight flag so we can retry after auth.
        if (data.error === 'auth_required') {
          historyInFlightRef.current = false;
          pendingHistoryRequestIdRef.current = null;
          setIsLoadingHistory(false);
        }
        setMessages(prev => [...prev, {
          id: `error-${Date.now()}`,
          type: 'error',
          text: data.error,
          timestamp: new Date().toISOString(),
          channel: 'web',
        }]);
        break;

      default:
        console.warn('Unknown message type:', data.type);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadInitialHistory, hasLoadedInitialHistory, persistedSessionID, sessionID, voiceEnabled, speakReplies, speakText]);

  // Bind socket handlers AFTER callbacks are defined to avoid TDZ issues
  useEffect(() => {
    if (!socket) return;

    const handleMessage = (event) => {
      try {
        // The backend should send exactly one JSON object per message.
        // However, be defensive and support newline-delimited batches as well.
        const raw = (event.data || '').toString();
        const parts = raw.split('\n').map(s => s.trim()).filter(Boolean);
        for (const p of parts) {
          const data = JSON.parse(p);
          handleWebSocketMessage(data);
        }
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    };

    const handleError = (error) => {
      console.error('WebSocket error:', error);
    };

    socket.onmessage = handleMessage;
    socket.onerror = handleError;

    return () => {
      if (socket) {
        socket.onmessage = null;
        socket.onerror = null;
      }
    };
  }, [socket, handleWebSocketMessage]);

  const handleSendMessage = (text) => {
    if (!text.trim() || !isConnected) return;
    console.log('[Chat] sending user message', text);

    // Add user message to UI immediately
    const userMessage = {
      id: `user-${Date.now()}`,
      type: 'user',
      text: text,
      timestamp: new Date().toISOString(),
      channel: 'web',
    };
    setMessages(prev => [...prev, userMessage]);

    // Clear input
    setInputValue('');

    // Send to server
    sendMessage({
      type: 'chat',
      message: text,
    });
  };

  const loadMoreHistory = async () => {
    if (!hasMoreHistory || isLoadingHistory || !historyCursor) return;
    if (!isAuthed) return;
    requestHistoryPage({
      before_id: historyCursor,
    });
  };

  // STT (SpeechRecognition)
  const sttSupported = typeof window !== 'undefined' && (
    window.SpeechRecognition || window.webkitSpeechRecognition
  );

  const ensureRecognition = useCallback(() => {
    if (!sttSupported) return null;
    if (recognitionRef.current) return recognitionRef.current;
    const Rec = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new Rec();
    rec.lang = 'en-US';
    rec.continuous = (listenMode === 'auto');
    rec.interimResults = true;

    rec.onstart = () => {
      setIsRecognizing(true);
      setInterimText('');
      finalBufferRef.current = '';
    };

    rec.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const transcript = result[0].transcript;
        if (result.isFinal) {
          finalBufferRef.current = (finalBufferRef.current + ' ' + transcript).trim();
        } else {
          interim += transcript;
        }
      }
      setInterimText(interim);
      const composed = [finalBufferRef.current, interim].filter(Boolean).join(' ').trim();
      setInputValue(composed);
    };

    rec.onerror = (e) => {
      console.warn('SpeechRecognition error', e);
    };

    rec.onend = () => {
      setIsRecognizing(false);
      setInterimText('');
      const finalText = finalBufferRef.current.trim();
      if (finalText) {
        handleSendMessage(finalText);
        finalBufferRef.current = '';
      }
      // Auto-restart in always listening mode if voice is still enabled
      if (voiceEnabled && listenMode === 'auto') {
        // small delay to avoid rapid restarts
        setTimeout(() => {
          try { rec.start(); } catch (e) { /* ignore */ }
        }, 250);
      }
    };

    recognitionRef.current = rec;
    return rec;
  // keep deps minimal; handleSendMessage is stable enough for this use.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listenMode, sttSupported]);

  const startRecognition = useCallback(() => {
    if (!voiceEnabled) return;
    const rec = ensureRecognition();
    if (!rec) return;
    try {
      // update continuous setting per mode
      rec.continuous = (listenMode === 'auto');
      rec.start();
    } catch (e) {
      // start may throw if already started
      console.debug('startRecognition ignored', e?.message || e);
    }
  }, [ensureRecognition, listenMode, voiceEnabled]);

  const stopRecognition = useCallback(() => {
    const rec = recognitionRef.current;
    if (!rec) return;
    try { rec.stop(); } catch { /* ignore */ }
  }, []);

  // React to voice mode toggles
  useEffect(() => {
    localStorage.setItem('voice_enabled', JSON.stringify(voiceEnabled));
    if (!voiceEnabled) {
      stopRecognition();
      window.speechSynthesis && window.speechSynthesis.cancel();
    } else if (listenMode === 'auto') {
      // start listening immediately in auto mode
      startRecognition();
    }
  }, [voiceEnabled, listenMode, startRecognition, stopRecognition]);

  useEffect(() => {
    localStorage.setItem('voice_listen_mode', listenMode);
  }, [listenMode]);

  useEffect(() => {
    localStorage.setItem('voice_speak_replies', JSON.stringify(speakReplies));
  }, [speakReplies]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopRecognition();
      window.speechSynthesis && window.speechSynthesis.cancel();
    };
  }, [stopRecognition]);

  // UI controls handlers
  const toggleVoiceEnabled = () => setVoiceEnabled(v => !v);
  const handleListenModeChange = (e) => setListenMode(e.target.value);
  const handleSpeakRepliesChange = (e) => setSpeakReplies(e.target.checked);

  // PTT button handlers (works as hold-to-talk and click-to-toggle)
  const [pttHeld, setPttHeld] = useState(false);
  const handlePTTDown = () => {
    if (!voiceEnabled) setVoiceEnabled(true);
    setPttHeld(true);
    startRecognition();
  };
  const handlePTTUp = () => {
    setPttHeld(false);
    stopRecognition();
  };
  const handlePTTClick = () => {
    // Tap to toggle start/stop for touch devices
    if (!isRecognizing) {
      handlePTTDown();
    } else {
      handlePTTUp();
    }
  };

  const sttAvailable = !!sttSupported;

  return (
    <div className="chat-container">
      <div className="chat-header">
        <div>
          <h2>Chat with Your Medical Assistant</h2>
          {userName && <><div className="user-name">Welcome, George P. Burdell</div><div style={{ visibility: 'hidden' }}>{userName}</div></>}
        </div>
        <div className="header-right">
          <div className={`connection-status ${isConnected ? 'connected' : 'disconnected'}`}>
            {isConnected ? '● Connected' : '○ Disconnected'}
          </div>

		  {/* Optional: show prefetch status to reassure user that context is warming */}
		  {isAuthed && (
			  <div className="prefetch-status" style={{ fontSize: '12px', opacity: 0.8, marginTop: '4px' }}>
				{prefetchState?.status === 'running' && 'Loading your context…'}
				{prefetchState?.status === 'done' && 'Context ready'}
				{prefetchState?.status === 'error' && 'Context load had issues (continuing)'}
			  </div>
		  )}
          <div className="voice-controls">
            <label className={`toggle ${voiceEnabled ? 'on' : ''}`}>
              <input
                type="checkbox"
                checked={voiceEnabled}
                onChange={toggleVoiceEnabled}
                disabled={!sttAvailable}
                title={sttAvailable ? 'Enable voice mode' : 'Speech Recognition not supported in this browser'}
              />
              Voice
            </label>
            {voiceEnabled && (
              <>
                <select
                  className="listen-mode"
                  value={listenMode}
                  onChange={handleListenModeChange}
                >
                  <option value="ptt">Push-to-talk</option>
                  <option value="auto">Always listening</option>
                </select>
                <label className="speak-replies">
                  <input type="checkbox" checked={speakReplies} onChange={handleSpeakRepliesChange} />
                  Speak replies
                </label>
                {listenMode === 'ptt' && (
                  <button
                    type="button"
                    className={`ptt-button ${isRecognizing ? 'listening' : ''}`}
                    onMouseDown={handlePTTDown}
                    onMouseUp={handlePTTUp}
                    onMouseLeave={() => pttHeld && handlePTTUp()}
                    onTouchStart={handlePTTDown}
                    onTouchEnd={handlePTTUp}
                    onClick={handlePTTClick}
                    title="Hold to talk (or tap to start/stop)"
                    disabled={!sttAvailable}
                  >
                    {isRecognizing ? 'Listening… Release to send' : 'Hold to talk'}
                  </button>
                )}
                {listenMode === 'auto' && (
                  <div className={`listening-indicator ${isRecognizing ? 'on' : 'off'}`}>
                    {isRecognizing ? 'Listening…' : 'Idle'}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <MessageList 
        messages={messages}
        onLoadMore={loadMoreHistory}
        hasMore={hasMoreHistory}
        isLoadingMore={isLoadingHistory}
      />
      {voiceEnabled && interimText && (
        <div className="interim-transcript">{interimText}</div>
      )}
      <MessageInput 
        onSend={handleSendMessage} 
        disabled={!isConnected}
        value={inputValue}
        onChangeValue={setInputValue}
        placeholder={isConnected ? "Ask about your health, medications, or care plan..." : "Connecting..."}
      />
    </div>
  );
}

export default Chat;
